/*
 * Navbar Component
 *
 * Changes:
 * - Added "Gallery" link to desktop navigation between Tours and Reviews
 * - Added "Gallery" section to mobile menu navigation
 * - All gallery links scroll to #gallery section on homepage
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Menu, X, ChevronDown, CalendarRange, ChevronRight } from 'lucide-react';
import { useNavigate, Link, useLocation, useMatch } from 'react-router-dom';
import { useBackgroundColor } from '../hooks/useBackgroundColor';
import { supabase } from '../lib/supabase';
import { Tour } from '../types/tours';

interface NavbarProps {
  isTransparent?: boolean;
  hasWhiteBackground?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ isTransparent = false, hasWhiteBackground = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isIndexPage = useMatch('/');
  const [pendingScroll, setPendingScroll] = useState<string | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(false);
  const [activeMoreDropdown, setActiveMoreDropdown] = useState(false);
  const navbarRef = useRef<HTMLDivElement>(null);
  const isLightBackground = useBackgroundColor(navbarRef);
  const [tours, setTours] = useState<Tour[]>([]);
  const [toursLoading, setToursLoading] = useState(true);

  const handleLogoClick = () => {
    if (isIndexPage) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  const scrollToSection = (sectionId: string) => {
    if (!isIndexPage) {
      setPendingScroll(sectionId);
      navigate('/', { replace: true });
    } else {
      const section = document.getElementById(sectionId) || 
        document.querySelector(`section:has(#${sectionId})`) ||
        document.querySelector(`.bg-\\[\\#F2F3F4\\]`);
      section?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (isIndexPage && pendingScroll) {
      // Small delay to ensure the page has loaded
      const scrollTimer = setTimeout(() => {
        const section = document.getElementById(pendingScroll) || 
          document.querySelector(`section:has(#${pendingScroll})`) ||
          document.querySelector(`.bg-\\[\\#F2F3F4\\]`);
        if (section) {
          section.scrollIntoView({ behavior: 'smooth' });
        }
        setPendingScroll(null);
      }, 500); // Increased delay to ensure content is rendered
      return () => clearTimeout(scrollTimer);
    }
  }, [isIndexPage, pendingScroll]);

  // Fetch tours
  useEffect(() => {
    const fetchTours = async () => {
      try {
        const { data, error } = await supabase
          .from('tours')
          .select('id, name, slug')
          .order('display_order', { ascending: true })
          .order('created_at', { ascending: false });

        if (error) throw error;
        setTours(data || []);
      } catch (err) {
        console.error('Error fetching tours:', err);
      } finally {
        setToursLoading(false);
      }
    };

    fetchTours();
  }, []);

  // Set page as loaded after initial render
  useEffect(() => {
    const loadTimer = setTimeout(() => {
      setPageLoaded(true);
    }, 100);
    return () => clearTimeout(loadTimer);
  }, []);

  const moreItems = [
    "Sierpe Frogs New Species",
    "Scientific work",
    "Amphibians of the Osa Peninsula"
  ];

  const languages = [
    { code: 'en', flag: '🇺🇸' },
    { code: 'es', flag: '🇪🇸' },
    { code: 'nl', flag: '🇳🇱' },
    { code: 'de', flag: '🇩🇪' }
  ];

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      setIsScrolled(window.innerWidth < 768 || window.scrollY > 50);
    };

    const handleScroll = () => {
      const scrolled = window.scrollY > 50;
      setIsScrolled(isMobile || scrolled);
      
      if (!scrolled && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', checkMobile);
    
    checkMobile();
    
    const timer = setTimeout(() => {
      setHeaderVisible(true);
    }, 500);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkMobile);
      clearTimeout(timer);
    };
  }, [isMenuOpen, isMobile]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const menu = document.querySelector('.mobile-menu');
      const menuButton = document.querySelector('.menu-button');
      
      if (isMenuOpen && menu && menuButton && 
          !menu.contains(event.target as Node) && 
          !menuButton.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  return (
    <div 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ease-out ${
        headerVisible ? 'translate-y-0' : '-translate-y-full'
      } w-[95vw] md:w-[90vw] lg:w-[80vw] xl:w-[70vw] mx-auto`}>
      <div className={`transition-all duration-700 ${isScrolled ? 'w-[70vw] md:w-[50vw] xl:w-[30vw] mx-auto' : 'container mx-auto px-4'}`}>
        <div 
          ref={navbarRef}
          className={`${
            isTransparent 
              ? (isLightBackground ? 'bg-black/50' : 'bg-black/30') + ' backdrop-blur-sm transition-colors duration-300 ease-in-out' 
              : 'bg-[#2A382A]'
          } rounded-[2rem] py-2 px-4 md:px-8 my-2`}
        >
          <div className="flex justify-between items-center">
            {/* Logo */}
            <button 
              onClick={handleLogoClick}
              className="font-barrio text-2xl md:text-3xl relative cursor-pointer"
              title="Sierpe Frogs"
            >
              <span className="block whitespace-nowrap">
                {isScrolled ? (
                  <span className="flex items-center gap-2">
                    <img src="/assets/logo-inversed-new.webp" alt="Sierpe Frogs Logo" className="w-10 h-10" />
                  </span>
                ) : (
                  <>
                    <span className="hidden md:inline flex items-center gap-3">
                      <img src="/assets/logo-inversed-new.webp" alt="Sierpe Frogs Logo" className="inline-block w-10 h-10 mr-2" />
                      SIERPE FROGS
                    </span>
                  </>
                )}
              </span>
            </button>

            {/* Center Navigation */}
            <div className={`hidden ${isScrolled ? '' : 'lg:flex'} items-center gap-4 md:gap-8 ml-auto mr-6`}>
              <div>
                <button
                  onClick={() => {
                    scrollToSection('adventures');
                  }}
                  className="text-white font-bold relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-[#E85C0D] after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300 after:origin-left"
                >
                  Tours
                </button>
              </div>
              <button
                onClick={() => {
                  scrollToSection('gallery');
                }}
                className="text-white font-bold relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-[#E85C0D] after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300 after:origin-left"
              >
                Gallery
              </button>
              <button
                onClick={() => {
                  scrollToSection('testimonials');
                }}
                className="text-white font-bold relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-[#E85C0D] after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300 after:origin-left"
              >
                Reviews
              </button>
              <div className="hidden lg:block">
                <button
                  onClick={() => {
                    scrollToSection('contact');
                  }}
                  className="text-white font-bold relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-[#E85C0D] after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300 after:origin-left"
                >
                  Contact
                </button>
              </div>
            </div>

            {/* Right Section */}
            <div className="flex items-center justify-end gap-6">
              {/* Menu Toggle */}
              <div className="relative flex items-center justify-center">
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  aria-label={isMenuOpen ? "Close menu" : "Open menu"}
                  className="menu-button w-10 h-10 md:w-10 md:h-10 hover:bg-black/30 rounded-[1rem] transition-all duration-300 flex items-center justify-center"
                >
                  {isMenuOpen ? 
                    <X className="w-7 h-7 md:w-6 md:h-6" /> : 
                    <Menu className="w-7 h-7 md:w-6 md:h-6" />
                  }
                </button>
              </div>
            </div>

            {/* Mobile Menu */}
            <div 
              className={`
                mobile-menu absolute top-full left-0 right-0 mt-2 bg-black/90 backdrop-blur-sm rounded-[2rem]
                max-h-[calc(100vh-100px)] overflow-y-auto scrollbar-hide overflow-x-hidden
                transition-all duration-500 origin-top
                ${isMenuOpen ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-0 pointer-events-none'}
              `}
            >
              <div className="p-4 md:p-6">
                {/* Tours */}
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <ChevronRight className="w-5 h-5 text-white" />
                    Tours
                  </h3>
                  <div className="space-y-1 md:space-y-2">
                    {tours.map((tour) => (
                      <a 
                        key={tour.id}
                        href={`/tours/${tour.slug}`}
                        className="block py-1.5 md:py-2 pl-8 md:pl-10 relative"
                      >
                        <span className="inline-block relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-[#E85C0D] after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300 after:origin-left">
                          {tour.name}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>

                {/* About Us */}
                <div className="mt-2 space-y-1 md:space-y-2">
                  <a href="/about-us" className="flex items-center gap-2 text-lg font-semibold py-2 relative">
                    <ChevronRight className="w-5 h-5 text-white" />
                    <span className="inline-block relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-[#E85C0D] after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300 after:origin-left">About Us</span>
                  </a>
                </div>

                {/* News */}
                <div className="mt-2 space-y-1 md:space-y-2">
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      scrollToSection('news');
                    }}
                    className="flex items-center gap-2 text-lg font-semibold py-2 relative w-full text-left"
                  >
                    <ChevronRight className="w-5 h-5 text-white" />
                    <span className="inline-block relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-[#E85C0D] after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300 after:origin-left">News</span>
                  </button>
                </div>

                {/* Gallery */}
                <div className="mt-2 space-y-1 md:space-y-2">
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      scrollToSection('gallery');
                    }}
                    className="flex items-center gap-2 text-lg font-semibold py-2 relative w-full text-left"
                  >
                    <ChevronRight className="w-5 h-5 text-white" />
                    <span className="inline-block relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-[#E85C0D] after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300 after:origin-left">Gallery</span>
                  </button>
                </div>

                {/* Reviews */}
                <div className="mt-2 space-y-1 md:space-y-2">
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      scrollToSection('testimonials');
                    }}
                    className="flex items-center gap-2 text-lg font-semibold py-2 relative w-full text-left"
                  >
                    <ChevronRight className="w-5 h-5 text-white" />
                    <span className="inline-block relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-[#E85C0D] after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300 after:origin-left">Reviews</span>
                  </button>
                </div>

                {/* Contact */}
                <div className="mt-2 space-y-1 md:space-y-2">
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      scrollToSection('contact');
                    }}
                    className="flex items-center gap-2 text-lg font-semibold py-2 relative w-full text-left"
                  >
                    <ChevronRight className="w-5 h-5 text-white" />
                    <span className="inline-block relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-[#E85C0D] after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300 after:origin-left">Contact</span>
                  </button>
                </div>

                {/* Scientific Work */}
                <div className="mt-2 space-y-1 md:space-y-2">
                  <a href="/scientific-work" className="flex items-center gap-2 text-lg font-semibold py-2 relative">
                    <ChevronRight className="w-5 h-5 text-white" />
                    <span className="inline-block relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-[#E85C0D] after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300 after:origin-left">Scientific Work</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Navbar;